import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
} from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { ToolHeading } from '@/shared/ui/ToolHeading'
import { ClipboardFeedbackOwner } from '@/pages/qr-scanner/clipboardFeedback'
import { trackScannerRejection } from '@/pages/qr-scanner/scannerAnalytics'
import { CameraRequestError, requestCameraWithTimeout } from '@/pages/qr-scanner/scannerCamera'
import {
  createScannerDecoder,
  ScannerDecoderCancelledError,
  ScannerDecoderWorkerError,
} from '@/pages/qr-scanner/scannerDecoder'
import {
  ScannerOperationOwner,
  type ScannerOperationToken,
} from '@/pages/qr-scanner/scannerOperationOwner'
import {
  analyticsErrorFor,
  barcodeAnalyticsFormat,
  inspectImageStructure,
  parseSafeUrl,
  validateImageFile,
  type DecodeResult,
  type DecodedCandidate,
  type ScanErrorCode,
  type ScanResult,
  type ScannerMode,
} from '@/pages/qr-scanner/scannerModel'

const CAMERA_TIMEOUT_MS = 30_000
const MAX_CAMERA_FRAME_SIDE = 1_600

export function QrScannerPage() {
  return <ScannerPage mode="qr" />
}

export function BarcodeScannerPage() {
  return <ScannerPage mode="barcode" />
}

function ScannerPage({ mode }: { mode: ScannerMode }) {
  const t = useT()
  const copy = mode === 'barcode' ? t.barcodeScanner : t.scanner
  const schema = mode === 'barcode' ? t.barcodeScannerSchema : t.scannerSchema
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const scanTimerRef = useRef<number | null>(null)
  const cameraTimeoutRef = useRef<number | null>(null)
  const clipboardFeedbackRef = useRef<ClipboardFeedbackOwner | null>(null)
  if (!clipboardFeedbackRef.current) clipboardFeedbackRef.current = new ClipboardFeedbackOwner()
  const clipboardFeedback = clipboardFeedbackRef.current
  const operationOwnerRef = useRef<ScannerOperationOwner | null>(null)
  if (!operationOwnerRef.current) {
    operationOwnerRef.current = new ScannerOperationOwner(
      () => createScannerDecoder(undefined, mode),
      (inputMethod) => startToolOperation(scannerAnalyticsContext(mode, inputMethod)),
    )
  }
  const operationOwner = operationOwnerRef.current
  const [result, setResult] = useState<ScanResult | null>(null)
  const [error, setError] = useState<ScanErrorCode | null>(null)
  const [isReadingFile, setIsReadingFile] = useState(false)
  const [isCameraStarting, setIsCameraStarting] = useState(false)
  const [isCameraActive, setIsCameraActive] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [copiedCandidateIndex, setCopiedCandidateIndex] = useState<number | null>(null)
  const [candidates, setCandidates] = useState<DecodedCandidate[]>([])
  const resultRef = useRef<HTMLElement | null>(null)
  const pasteHandlerRef = useRef<(event: ClipboardEvent) => void>(() => undefined)
  pasteHandlerRef.current = handlePaste

  useEffect(() => () => {
    clipboardFeedback.invalidate()
    operationOwner.cancel('cancelled')
    stopCameraMedia(false)
  }, [clipboardFeedback, operationOwner])

  useEffect(() => {
    const listener = (event: ClipboardEvent) => pasteHandlerRef.current(event)
    window.addEventListener('paste', listener)
    return () => window.removeEventListener('paste', listener)
  }, [])

  useEffect(() => {
    if (!result && !error && candidates.length === 0) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    resultRef.current?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'nearest',
    })
  }, [result, error, candidates])

  const destination = mode === 'qr' && result ? parseSafeUrl(result.text) : null

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    event.target.value = ''
    await scanImageFile(file)
  }

  function handlePaste(event: ClipboardEvent) {
    const file = Array.from(event.clipboardData?.files ?? []).find(isImageFile)
    if (file) void scanImageFile(file)
    else rejectClipboard('clipboard_empty')
  }

  async function pasteFromClipboard() {
    if (!navigator.clipboard?.read) {
      rejectClipboard('clipboard_unavailable')
      return
    }

    let file: File | null
    try {
      file = await readClipboardImage(await navigator.clipboard.read())
    } catch {
      rejectClipboard('clipboard_unavailable')
      return
    }
    if (file) await scanImageFile(file)
    else rejectClipboard('clipboard_empty')
  }

  function rejectClipboard(code: 'clipboard_empty' | 'clipboard_unavailable') {
    resetForNewInput()
    setError(code)
    trackRejection(mode, 'upload', analyticsErrorFor(code))
  }

  function resetForNewInput() {
    cancelActiveOperation()
    setError(null)
    setResult(null)
    setCandidates([])
    setIsCopied(false)
    setCopiedCandidateIndex(null)
  }

  async function scanImageFile(file: File) {
    resetForNewInput()
    const validationError = validateImageFile(file)
    if (validationError) {
      setError(validationError)
      trackRejection(mode, 'upload', analyticsErrorFor(validationError))
      return
    }

    let token: ScannerOperationToken
    try {
      token = operationOwner.begin('upload', performance.now())
    } catch {
      setError('scanner_failed')
      trackRejection(mode, 'upload', 'processing_failed')
      return
    }
    setIsReadingFile(true)
    try {
      const structureError = await inspectImageStructure(file)
      if (!operationOwner.isCurrent(token)) return
      if (structureError) {
        setIsReadingFile(false)
        setError(structureError)
        operationOwner.error(token, analyticsErrorFor(structureError))
        return
      }

      const decoded = await token.decoder.decode(file)
      if (!operationOwner.isCurrent(token)) return
      setIsReadingFile(false)
      applyDecodeResult(decoded, token)
    } catch (cause) {
      if (!operationOwner.isCurrent(token) || cause instanceof ScannerDecoderCancelledError) return
      const scannerError = new ScannerError(
        cause instanceof ScannerDecoderWorkerError ? 'scanner_failed' : 'unsupported_file',
      )
      setIsReadingFile(false)
      setError(scannerError.code)
      operationOwner.error(token, analyticsErrorFor(scannerError.code))
    }
  }

  async function startCamera() {
    const mediaDevices = navigator.mediaDevices
    resetForNewInput()

    if (!mediaDevices?.getUserMedia) {
      setError('unsupported_browser')
      trackRejection(mode, 'camera', 'unsupported_input')
      return
    }

    let token: ScannerOperationToken
    try {
      token = operationOwner.begin('camera', performance.now())
    } catch {
      setError('camera_failed')
      trackRejection(mode, 'camera', 'processing_failed')
      return
    }
    cameraTimeoutRef.current = window.setTimeout(() => {
      if (!operationOwner.isCurrent(token)) return
      stopCameraMedia()
      setIsCameraStarting(false)
      setError('camera_timeout')
      operationOwner.error(token, 'timeout')
    }, CAMERA_TIMEOUT_MS)

    try {
      setIsCameraStarting(true)
      const stream = await requestCameraWithTimeout(mediaDevices, CAMERA_TIMEOUT_MS)
      if (!operationOwner.isCurrent(token)) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      streamRef.current = stream
      const video = videoRef.current
      if (!video) throw new ScannerError('camera_failed')

      video.srcObject = stream
      await video.play()
      if (!operationOwner.isCurrent(token)) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      setIsCameraActive(true)
      setIsCameraStarting(false)
      scanCameraFrame(token)
    } catch (cause) {
      if (!operationOwner.isCurrent(token)) return
      const scannerError = normalizeCameraError(cause)
      stopCameraMedia()
      setIsCameraStarting(false)
      setError(scannerError.code)
      operationOwner.error(token, analyticsErrorFor(scannerError.code))
    }
  }

  function stopCameraMedia(updateState = true) {
    if (cameraTimeoutRef.current !== null) {
      window.clearTimeout(cameraTimeoutRef.current)
      cameraTimeoutRef.current = null
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (scanTimerRef.current !== null) {
      window.clearTimeout(scanTimerRef.current)
      scanTimerRef.current = null
    }
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
    if (updateState) setIsCameraActive(false)
  }

  function cancelActiveOperation() {
    clipboardFeedback.invalidate()
    operationOwner.cancel('cancelled')
    stopCameraMedia()
    setIsReadingFile(false)
    setIsCameraStarting(false)
  }

  function scanCameraFrame(token: ScannerOperationToken) {
    const tick = async () => {
      if (!operationOwner.isCurrent(token)) return
      const video = videoRef.current
      const canvas = canvasRef.current
      const elapsed = performance.now() - token.startedAt
      if (elapsed >= CAMERA_TIMEOUT_MS) {
        stopCameraMedia()
        setError('camera_timeout')
        operationOwner.error(token, 'timeout')
        return
      }

      if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      try {
        const scale = Math.min(1, MAX_CAMERA_FRAME_SIDE / Math.max(video.videoWidth, video.videoHeight))
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
        const context = canvas.getContext('2d', { willReadFrequently: true })
        if (!context) throw new ScannerError('camera_failed')
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const decoded = await token.decoder.decode(context.getImageData(0, 0, canvas.width, canvas.height))
        if (!operationOwner.isCurrent(token)) return
        if (decoded.kind !== 'none') {
          stopCameraMedia()
          applyDecodeResult(decoded, token)
          return
        }
      } catch (cause) {
        if (!operationOwner.isCurrent(token) || cause instanceof ScannerDecoderCancelledError) return
        stopCameraMedia()
        setError(cause instanceof ScannerDecoderWorkerError ? 'scanner_failed' : 'camera_failed')
        operationOwner.error(token, 'processing_failed')
        return
      }

      if (operationOwner.isCurrent(token)) {
        if (mode === 'barcode') scanTimerRef.current = window.setTimeout(tick, 200)
        else rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }

  function applyDecodeResult(
    decoded: DecodeResult,
    token: ScannerOperationToken,
  ) {
    if (decoded.kind === 'none') {
      setError('no_qr_found')
      operationOwner.error(token, 'processing_failed')
      return
    }
    if (decoded.kind === 'multiple') {
      if (mode === 'barcode') {
        if (!operationOwner.success(token, performance.now() - token.startedAt)) return
        setError(null)
        setCandidates(decoded.candidates ?? [])
        return
      }
      setError('multiple_codes')
      operationOwner.error(token, 'processing_failed')
      return
    }

    setError(null)
    if (mode === 'barcode' && !decoded.format) {
      setError('scanner_failed')
      operationOwner.error(token, 'processing_failed')
      return
    }
    const successDimensions = mode === 'barcode' && decoded.format
      ? { detectedFormat: barcodeAnalyticsFormat(decoded.format) }
      : undefined
    if (!operationOwner.success(
      token,
      performance.now() - token.startedAt,
      successDimensions,
    )) return
    setCandidates([])
    setResult({
      text: decoded.text ?? '',
      inputMethod: token.inputMethod,
      format: decoded.format,
    })
  }

  async function copyResult() {
    if (!result) return
    setCopiedCandidateIndex(null)
    await clipboardFeedback.copy(
      result.text,
      (value) => navigator.clipboard.writeText(value),
      setIsCopied,
    )
  }

  async function copyCandidate(candidate: DecodedCandidate, index: number) {
    setIsCopied(false)
    setCopiedCandidateIndex(null)
    await clipboardFeedback.copy(
      candidate.text,
      (value) => navigator.clipboard.writeText(value),
      (copied) => setCopiedCandidateIndex(copied ? index : null),
    )
  }

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHeading title={copy.hero.title} description={copy.hero.description} />

      <div className="mt-4 grid gap-4 sm:mt-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-6">
        <section className="min-w-0 space-y-4">
          <Card className="rounded-lg">
            <CardContent className="space-y-3 p-4 sm:p-5">
              <input
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/webp"
                className="sr-only ym-disable-keys ym-disable-submit"
                onChange={handleFileChange}
                type="file"
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  className="w-full sm:col-span-2"
                  disabled={isReadingFile}
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                >
                  {isReadingFile ? copy.upload.reading : copy.upload.choose}
                </Button>
                <Button
                  className="w-full"
                  disabled={isReadingFile}
                  onClick={() => void pasteFromClipboard()}
                  type="button"
                  variant="outline"
                >
                  {copy.upload.paste}
                </Button>
                <Button
                  className="w-full"
                  disabled={isCameraActive || isCameraStarting}
                  onClick={startCamera}
                  type="button"
                  variant="outline"
                >
                  {isCameraStarting ? copy.camera.starting : copy.camera.start}
                </Button>
              </div>
              <p className="text-xs leading-5 text-muted-foreground">
                {copy.upload.dropDescription}{' '}
                <span className="[@media(pointer:coarse)]:hidden">{copy.upload.pasteHint}</span>{' '}
                {copy.camera.permissionHint}
              </p>
            </CardContent>
          </Card>

          <Card className={cn('rounded-lg', !isCameraActive && 'hidden')}>
              <CardHeader>
                <CardTitle>{copy.camera.previewTitle}</CardTitle>
                <CardDescription>{copy.camera.placeholder}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="ym-hide-content overflow-hidden rounded-lg border bg-slate-950">
                  <video ref={videoRef} className={cn('aspect-video w-full object-cover')} muted playsInline />
                </div>
                <Button className="w-full" onClick={cancelActiveOperation} type="button" variant="outline">
                  {copy.camera.stop}
                </Button>
              </CardContent>
          </Card>
          <canvas ref={canvasRef} className="hidden" />
        </section>

        <aside ref={resultRef} className="min-w-0 scroll-mt-4 lg:sticky lg:top-6 lg:self-start">
          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle>{copy.result.title}</CardTitle>
              <CardDescription>{copy.result.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {copy.errors[error]}
                </div>
              )}
              {!result && candidates.length === 0 && !error && (
                <div className="grid min-h-24 place-items-center lg:min-h-44 rounded-lg border border-dashed bg-background p-6 text-center text-sm leading-6 text-muted-foreground">
                  {copy.result.empty}
                </div>
              )}
              {result && (
                <div className="ym-hide-content space-y-4">
                  <div className="rounded-lg border bg-background p-3">
                    <div className="text-xs font-medium uppercase tracking-normal text-muted-foreground">
                      {result.inputMethod === 'camera' ? copy.result.sourceCamera : copy.result.sourceUpload}
                    </div>
                    {result.format && <div className="mt-2 text-sm font-semibold">{result.format}</div>}
                    <div className="mt-2 max-h-52 overflow-auto break-words font-mono text-sm">{result.text}</div>
                  </div>
                  {destination && (
                    <div className="rounded-lg border bg-background p-3 text-sm">
                      <div className="font-medium">{copy.result.destination}</div>
                      <div className="mt-1 break-words text-muted-foreground">{destination.host}</div>
                      <Button asChild className="mt-3 w-full" variant="outline">
                        <a
                          className="ym-disable-tracklink"
                          href={destination.href}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {copy.result.openLink}
                        </a>
                      </Button>
                    </div>
                  )}
                  <Button className="w-full" onClick={copyResult} type="button">
                    {isCopied ? copy.result.copied : copy.result.copy}
                  </Button>
                </div>
              )}
              {candidates.length > 0 && (
                <div className="ym-hide-content space-y-3">
                  <div role="status" className="rounded-lg border border-amber-400/60 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
                    {copy.errors.multiple_codes}
                  </div>
                  {candidates.map((candidate, index) => (
                    <div key={`${candidate.format}-${candidate.text}-${index}`} className="rounded-lg border bg-background p-3">
                      <div className="text-sm font-semibold">{candidate.format}</div>
                      <div className="mt-1 break-all font-mono text-sm">{candidate.text}</div>
                      <Button
                        className="mt-3 w-full"
                        onClick={() => void copyCandidate(candidate, index)}
                        type="button"
                        variant="outline"
                      >
                        {copiedCandidateIndex === index ? copy.result.copied : copy.result.copy}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
      <section className="rounded-lg border bg-card p-5 shadow-sm">
        <h2 className="text-lg font-semibold tracking-normal">{copy.howToTitle}</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
          {schema.howTo.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </section>

      <section className="rounded-lg border bg-card p-5 shadow-sm">
        <h2 className="text-lg font-semibold tracking-normal">{copy.faqTitle}</h2>
        <div className="mt-3 space-y-3">
          {schema.faq.map((item) => (
            <div key={item.question}>
              <h3 className="text-sm font-semibold">{item.question}</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</p>
            </div>
          ))}
        </div>
        {mode === 'barcode' && (
          <a className="mt-4 inline-flex text-sm font-medium text-primary hover:underline" href="/skaner-qr-koda">
            {t.barcodeScanner.relatedQrScanner}
          </a>
        )}
      </section>
      </div>
    </div>
  )
}

function scannerAnalyticsContext(
  mode: ScannerMode,
  inputMethod: 'upload' | 'camera',
): ToolAnalyticsContext {
  if (mode === 'barcode') {
    return {
      app: 'qrka',
      domain: 'qrka.ru',
      route: '/skaner-shtrih-koda',
      utility: 'barcode-scanner',
      processingMode: 'local',
      inputMethod,
    }
  }
  return {
    app: 'qrka',
    domain: 'qrka.ru',
    route: '/skaner-qr-koda',
    utility: 'qr-scanner',
    processingMode: 'local',
    inputMethod,
  }
}

function trackRejection(
  mode: ScannerMode,
  inputMethod: 'upload' | 'camera',
  errorCode: Parameters<typeof trackToolRejection>[1],
): void {
  if (mode === 'qr') {
    trackScannerRejection(inputMethod, errorCode)
    return
  }
  trackToolRejection(scannerAnalyticsContext(mode, inputMethod), errorCode)
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

async function readClipboardImage(items: readonly ClipboardItem[]): Promise<File | null> {
  for (const item of items) {
    const type = item.types.find((value) => value.startsWith('image/'))
    if (type) return new File([await item.getType(type)], 'clipboard-image', { type })
  }
  return null
}

class ScannerError extends Error {
  constructor(readonly code: ScanErrorCode) {
    super(code)
  }
}

function normalizeCameraError(cause: unknown): ScannerError {
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return new ScannerError('permission_denied')
  }
  if (cause instanceof CameraRequestError) return new ScannerError(cause.code)
  return cause instanceof ScannerError ? cause : new ScannerError('camera_failed')
}
